import React, { createContext, useContext, useState, ReactNode } from 'react'

// Hardcoded list of available terminals (will be replaced with database query in the future)
const AVAILABLE_TERMINALS = ['99026532']

interface TerminalContextType {
  selectedTerminal: string | null // null = not selected yet (use fallback), empty string = explicitly "None" (don't include terminal_id), string = terminal ID
  setSelectedTerminal: (terminal: string | null) => void
  availableTerminals: string[]
}

const TerminalContext = createContext<TerminalContextType | undefined>(undefined)

const STORAGE_KEY = 'pos_selected_terminal'

export const TerminalProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [selectedTerminal, setSelectedTerminalState] = useState<string | null>(() => { // Can be string, empty string, or null
    // Load from localStorage on mount
    // null = not selected yet (will use fallback), empty string = explicitly "None" (don't include terminal_id)
    const stored = localStorage.getItem(STORAGE_KEY)
    if (stored === '') {
      return '' // Explicitly selected "None"
    }
    if (stored) {
      return stored // Previously stored terminal ID
    }
    // Auto-select if only one terminal is available
    if (AVAILABLE_TERMINALS.length === 1) {
      return AVAILABLE_TERMINALS[0]
    }
    return null // Not selected
  })

  const setSelectedTerminal = (terminal: string | null) => {
    setSelectedTerminalState(terminal)
    if (terminal !== null) {
      // Store empty string for "None", or the terminal ID
      localStorage.setItem(STORAGE_KEY, terminal)
    } else {
      // Remove from storage if explicitly set to null (shouldn't happen, but handle it)
      localStorage.removeItem(STORAGE_KEY)
    }
  }

  return (
    <TerminalContext.Provider
      value={{
        selectedTerminal,
        setSelectedTerminal,
        availableTerminals: AVAILABLE_TERMINALS
      }}
    >
      {children}
    </TerminalContext.Provider>
  )
}

export const useTerminal = (): TerminalContextType => {
  const context = useContext(TerminalContext)
  if (context === undefined) {
    throw new Error('useTerminal must be used within a TerminalProvider')
  }
  return context
}

